/**
 * Local useUpdateMetadataItem hook for OrgPilot (dev-agent).
 *
 * The Python agentic-platform does NOT yet expose a PUT /api/chat/metadata/items/{id}
 * endpoint, so this is a **stub** that optimistically returns the saved content.
 *
 * When the backend endpoint is added, replace the `mutationFn` body with a real
 * `api.put(...)` call in `devAgentService.ts` and import it here.
 *
 * The hook signature intentionally matches the old GraphQL-based
 * `useUpdateMetadataItem` so MetadataEditorPanel doesn't need changes.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { DEV_AGENT_BASE_URL, devAgentAuthHeaders } from "../devAgentService";
import { createAxiosInstance } from "@/dataProvider/axios";

// ---------- Types (matching old GraphQL shape) ----------

interface MetadataItemResult {
  id: string;
  metadataType: string;
  content: string;
  fullName: string;
  fileName: string;
  isEdited: boolean;
  status: string | null;
}

interface UseUpdateMetadataItemOptions {
  onSuccess?: (data: MetadataItemResult) => void;
  onError?: (error: Error) => void;
}

// ---------- API stub ----------

const api = createAxiosInstance(DEV_AGENT_BASE_URL);

/**
 * Attempt to save via PUT endpoint.  Falls back to a stub response when the
 * endpoint does not exist yet (404 → treat as success so the editor flow
 * doesn't break while the backend is being built).
 */
async function updateMetadataItem(input: {
  id: string;
  content: string;
}): Promise<MetadataItemResult> {
  try {
    const { data } = await api.put(
      `/api/chat/metadata/items/${encodeURIComponent(input.id)}`,
      { content: input.content },
      { headers: devAgentAuthHeaders() },
    );
    // Python backend wraps in { data: ... }
    const raw = data?.data ?? data;
    return {
      id: raw.id ?? input.id,
      metadataType: raw.metadata_type ?? "",
      content: raw.content ?? input.content,
      fullName: raw.full_name ?? "",
      fileName: raw.file_name ?? raw.full_name ?? "",
      isEdited: true,
      status: raw.status ?? null,
    };
  } catch (err: any) {
    // If the endpoint simply doesn't exist yet, return a stub so the
    // editor save flow can proceed (content is already in local state).
    if (err?.response?.status === 404 || err?.response?.status === 405) {
      console.debug(
        "[useUpdateMetadataItem] PUT endpoint not available yet — returning stub response.",
      );
      return {
        id: input.id,
        metadataType: "",
        content: input.content,
        fullName: "",
        fileName: "",
        isEdited: true,
        status: null,
      };
    }
    throw err;
  }
}

// ---------- Hook ----------

export function useUpdateMetadataItem(
  options?: UseUpdateMetadataItemOptions,
): UseMutationResult<MetadataItemResult, Error, { id: string; content: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMetadataItem,
    onSuccess: (data) => {
      if (data?.id) {
        // Invalidate the local devAgent query cache
        queryClient.invalidateQueries({
          queryKey: ["devAgent", "metadataItem", data.id],
        });
        queryClient.setQueryData(["devAgent", "metadataItem", data.id], data);
      }
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
